
import {NgModule} from '@angular/core';
import {CommonModule} from '@angular/common';
import {QuillModule} from 'ngx-quill';
import {RichTextEditorComponent} from './components/rich-text-editor/rich-text-editor.component';
import {VersionComponent} from './components/version/version.component';
import {TitleService} from './services/title/title.service';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';

import {
  AlertService,
  AuthGuardService,
  AuthService,
  Base64Service,
  BaseService,
  ModalDialogService,
  NavbarService,
  SpinnerService,
} from './services';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withXhr } from '@angular/common/http';
import {CredentialsInterceptor, ErrorInterceptor, SpinnerInterceptorService} from './interceptor';
import { EmailImpersonationInterceptor } from '../email/interceptors/email-impersonation.interceptor';
import {APP_INITIALIZER} from '@angular/core';
import {firstValueFrom} from 'rxjs';
import {BsDropdownModule} from 'ngx-bootstrap/dropdown';
import {PaginationModule} from 'ngx-bootstrap/pagination';
import {HeaderComponent} from './components/header/header.component';
import {EditProfileComponent} from './components/edit-profile/edit-profile.component';
import {ChangePasswordComponent} from './components/change-password/change-password.component';
import {RouterModule} from '@angular/router';
import {TableSortColumnComponent} from './components/table-sort-column/table-sort-column.component';
import {PaginationComponent} from './components/pagination/pagination.component';
import {ConsoleSearchDialogComponent} from './components/header/console-search-dialog/console-search-dialog.component';

import {ConfirmModalComponent} from './components/confirm-modal/confirm-modal.component';
import {TaskNoteModalComponent} from './components/task-note-modal/task-note-modal.component';
import {TaskNoteDirective} from './directives/task-note.directive';
import {BsModalService, ModalModule} from 'ngx-bootstrap/modal';
import {NgSelectModule} from '@ng-select/ng-select';
// NGX Bootstrap Datepicker
import {BsDatepickerModule} from 'ngx-bootstrap/datepicker';
import {DateAgoPipe} from './services/pipes/date-ago.pipe';
import {DecodeHtmlEntitiesPipe} from './services/pipes/decode-html-entities.pipe';
import {NumberDirective} from './services/directive/number.directive';
import {CurrencyInputDirective} from './services/directive/currency-input.directive';
import {AbnNumberPipe} from './services/pipes/abn-number.pipe';
import {PhonePipe} from './services/pipes/phone.pipe';
import {IMaskModule} from 'angular-imask';
import {BackendThemeOptions} from './backend-theme-options';
import {InformationDialogComponent} from './dialog/information-dialog/information-dialog.component';
import {NgbModule} from '@ng-bootstrap/ng-bootstrap';
import {AutocompleteLibModule} from 'angular-ng-autocomplete';
import {TooltipModule} from "ngx-bootstrap/tooltip";
import {LazyLoadImageModule} from 'ng-lazyload-image';
import {AutofocusDirective} from './services/directive/autofocus.directive';
import {ChangeQuantityComponent} from './components/change-quantity/change-quantity.component';
import {CommonSingleInputFormComponent} from './components/common-single-input-form/common-single-input-form.component';
import {ProductSelectPopupComponent} from './components/product-select-popup/product-select-popup.component';
import {AppDragDirective} from './directives/app-drag.directive';
import {DecimalInputDirective} from './directives/decimal-input.directive';
import {RatingGuideComponent} from './components/rating-guide/rating-guide.component'
import {NotFoundComponent} from './components/not-found/not-found.component';
import {NgxSummernoteModule} from 'ngx-summernote';
import {MobileNumberPipe} from './services/pipes/mobile-number.pipe';
// import {ActivityListComponent} from './components/activity-list/activity-list.component';
import {UploadFileDirective} from './directives/upload-file.directive';
import {NoteAttachmentListComponent} from './components/note-attachments/note-attachment-list/note-attachment-list.component';
import {NoteAttachmentPickerComponent} from './components/note-attachments/note-attachment-picker/note-attachment-picker.component';
import {UploadMultipleFileDirective} from './directives/upload-multiple-file.directive';
import {CachedSrcDirective} from './directives/cached-src.directive';
import {TaskDescriptionPipe} from "./services/pipes/task-description.pipe";
import {NotificationSidebarComponent} from './components/header/sidebar/notification-sidebar/notification-sidebar.component';
import {HighchartService} from './services/highchart.service';
import { NotificationsComponent } from './components/notifications/notifications.component';
import {NotificationFeedComponent} from './components/notification-feed/notification-feed.component';
import {DateRangeFilterComponent} from '../shared/components/date-range-filter/date-range-filter.component';
import {LoaderComponent} from '../shared/components/loader/loader.component';
import { ActivityDescriptionPipe } from './services/pipes/activity-description.pipe';
import { ProductLeadTimePipe } from './services/pipes/product-lead-time-pipe';
import { AccessoryNamePipe } from './services/pipes/accessory-name.pipe';
import { RescheduledNotesPipe } from './services/pipes/rescheduled-notes.pipe';
import { HasRescheduledTaskPipe, IsRescheduledTaskPipe, RescheduledTasksPipe } from './services/pipes/has-rescheduled-task.pipe';
import { NgxGpAutocompleteModule } from "@angular-magic/ngx-gp-autocomplete";
import { Loader } from '@googlemaps/js-api-loader';
import { environment } from 'src/environments/environment';
// import { MilestonesStatusComponent } from './components/milestones-status/milestones-status.component';
import {CopyContactDirective} from '../shared/copy-menu/copy-contact.directive';

export const interceptorProviders =
  [
    {provide: HTTP_INTERCEPTORS, useClass: CredentialsInterceptor, multi: true},
    // Stamps X-Impersonate-User-Id on Gmail requests while an owner is switched
    // into a rep's mailbox (email "switch account"). No-op otherwise.
    {provide: HTTP_INTERCEPTORS, useClass: EmailImpersonationInterceptor, multi: true},
    {provide: HTTP_INTERCEPTORS, useClass: SpinnerInterceptorService, multi: true},
    {provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true},
    {
      // Rebuild auth state from the HttpOnly cookie via /me (and fetch the cross-host service token)
      // before the app renders.
      provide: APP_INITIALIZER,
      useFactory: (auth: AuthService) => () => firstValueFrom(auth.init()),
      deps: [AuthService],
      multi: true
    },
  ];
//#region Directives
var directives = []

//#endregion
@NgModule({ declarations: [
        RichTextEditorComponent,
        VersionComponent,
        HeaderComponent,
        TableSortColumnComponent,
        PaginationComponent,
        ConfirmModalComponent,
        TaskNoteModalComponent,
        TaskNoteDirective,
        NumberDirective,
        CurrencyInputDirective,
        AppDragDirective,
        AbnNumberPipe,
        PhonePipe,
        InformationDialogComponent,
        AutofocusDirective,
        ChangeQuantityComponent,
        CommonSingleInputFormComponent,
        ProductSelectPopupComponent,
        RatingGuideComponent,
        NotFoundComponent,
        MobileNumberPipe,
        TaskDescriptionPipe,
        // ActivityListComponent,
        UploadMultipleFileDirective,
        CachedSrcDirective,
        ConsoleSearchDialogComponent,
        NotificationSidebarComponent,
        NotificationsComponent,
        NotificationFeedComponent,
        // ActivityDescriptionPipe,
        ProductLeadTimePipe,
        AccessoryNamePipe,
        RescheduledNotesPipe,
        HasRescheduledTaskPipe,
        IsRescheduledTaskPipe,
        RescheduledTasksPipe,
        DecodeHtmlEntitiesPipe,
        // MilestonesStatusComponent,
    ],
    exports: [
        RichTextEditorComponent,
        PhonePipe,
        AbnNumberPipe,
        NumberDirective,
        CurrencyInputDirective,
        AppDragDirective,
        AutofocusDirective,
        VersionComponent,
        HeaderComponent,
        TableSortColumnComponent,
        FormsModule,
        ReactiveFormsModule,
        ReactiveFormsModule,
         BsDropdownModule,
        RouterModule,
        PaginationComponent,
        PaginationModule,
        ConfirmModalComponent,
        TaskNoteDirective,
        ModalModule,
        NgSelectModule,
        BsDatepickerModule,
        IMaskModule,
        InformationDialogComponent,
        NgbModule,
        AutocompleteLibModule,
        TooltipModule,
        LazyLoadImageModule,
        RatingGuideComponent,
        NgxSummernoteModule,
        NotFoundComponent,
        MobileNumberPipe,
        // ActivityListComponent,
        UploadFileDirective,
        UploadMultipleFileDirective,
        // Standalone; re-exported so the module-based note hosts (shared modals,
        // accounts, contacts, pebbl-feedback) can use them without each adding
        // their own import.
        NoteAttachmentListComponent,
        NoteAttachmentPickerComponent,
        CachedSrcDirective,
        TaskDescriptionPipe,
        NotificationsComponent,
        // ActivityDescriptionPipe,
        ProductLeadTimePipe,
        AccessoryNamePipe,
        RescheduledNotesPipe,
        HasRescheduledTaskPipe,
        IsRescheduledTaskPipe,
        RescheduledTasksPipe,
        DecodeHtmlEntitiesPipe,
        NgxGpAutocompleteModule,
        // MilestonesStatusComponent
    ], imports: [
        NoteAttachmentListComponent,
        NoteAttachmentPickerComponent,
        CommonModule,
        QuillModule.forRoot(),
        EditProfileComponent,
        ChangePasswordComponent,
        UploadFileDirective,
        FormsModule,
        ReactiveFormsModule,
        BsDropdownModule,
        ModalModule,
        RouterModule,
        PaginationModule,
        NgSelectModule,
        BsDatepickerModule,
        IMaskModule,
        NgxGpAutocompleteModule,
        NgbModule,
        AutocompleteLibModule,
        TooltipModule,
         LazyLoadImageModule,
         DateAgoPipe,
         DecimalInputDirective,
        CopyContactDirective,
        // The View all notifications page's Received filter and first-load loader.
        DateRangeFilterComponent,
        LoaderComponent
        ],
        providers: [
        AlertService,
        // AuthService is intentionally NOT re-provided here: it is `providedIn: 'root'` and holds the
        // in-memory auth state. Listing it in a module that lazy-loaded feature modules import would
        // create a separate instance per lazy module (uninitialised => guards see "not authenticated").
        AuthGuardService,
        BaseService,
        Base64Service,
        AlertService,
        NavbarService,
        ModalDialogService,
        SpinnerService,
        TitleService,
        BsModalService,
        BackendThemeOptions,
        HighchartService,
        AccessoryNamePipe,
        interceptorProviders,
        {
            provide: Loader,
            useValue: new Loader({
                apiKey: environment.googleMapKey,
                libraries: ['places']
            })
        },
        // withXhr(): keep HttpClient on the XMLHttpRequest backend.
        //
        // Angular 19+ made `fetch` the default backend, and FetchBackend reads the
        // response body with `await reader.read()` on a native (unpatched) promise —
        // zone.js cannot restore the Angular zone across that await, so every response
        // is delivered in the root zone and no change detection follows. XHR is patched
        // by zone.js, so the callback lands back in the Angular zone. It is also the
        // transport this app was built on (HttpClientModule ≤ v18) and the only one that
        // supports upload progress, which FetchBackend throws on.
        provideHttpClient(withInterceptorsFromDi(), withXhr()),
    ] })


export class AdminCoreModule {
}
